/* ============================================================
   Pemilih waktu — jam dan minit sebagai dua senarai pendek.

   Ia menggantikan <input type="time"> di seluruh jadual v3.9.

   Sebabnya bukan rupa. Kotak jam browser membenarkan pengguna MENAIP
   mana-mana minit walaupun `step` ditetapkan, jadi satu-satunya
   perlindungan yang tinggal ialah membetulkan nilainya selepas dia
   menaip — dan pembetulan itu sampai kepadanya sebagai teguran atas
   kerja yang dia sangka betul ("Dilaraskan ke 08:05…"). Senarai ini
   tidak mengandungi minit yang salah langsung, jadi tiada apa yang perlu
   ditegur. Lihat LANGKAH_MINIT dalam scheduleV39.js untuk sebab
   kelipatan 5 minit itu wajib pada TikTok.

   Dua senarai pendek, bukan satu senarai penuh 288 waktu: senarai penuh
   bermakna menatal melepasi ~240 baris untuk sampai ke 20:15. Jam (24
   baris) dan minit (12 baris) sampai ke mana-mana waktu dengan dua klik.

   Ia turut menutup dua perkara kecil yang datang bersama kotak browser:
   formatnya ditetapkan oleh sistem operasi (di sini ia keluar sebagai
   "11:00 PM" dan terpotong dalam lajur sempit), dan anak panahnya tidak
   mengikut tema v3.9.
   ============================================================ */

import { JAM_PILIHAN, MINIT_PILIHAN, laraskanJam } from './scheduleV39';
import PilihV39 from './PilihV39';
import { useT } from '../i18n';

/* Lebar dua nombor + chevron, tanpa kotak separuh kosong di sebelah angka.
   ⚠️ Dimensi ditetapkan oleh "00", BUKAN "23": dalam Cal Sans, digit "0"
   ialah angka PALING LEBAR (8.53px @13px lwn ~7.5px untuk 2/3/8), jadi
   "00" (17.06px) lebih lebar daripada "08"/"23" (~15-16px). Pada 62px lama,
   ruang teks tinggal ~17px — "00" terpotong jadi "0…" sedangkan jam kekal
   penuh; kerana minit hampir selalu "00", kotak minit yang nampak rosak.
   68px memberi ~23px ruang teks — margin selesa untuk "00". Diukur pada
   font sebenar 2026-08-20. Tinggi datang dari pemanggil: baris tetapan
   modal (28px) dan kotak dalam lajur kalendar tidak sama. */
const LEBAR_ANGKA = 68;

export default function PilihWaktuV39({
    value = '09:00',
    onChange,
    /* Lalai `null` (bukan 'Waktu') supaya kata itu boleh datang daripada
       kamus di bawah. Pemanggil yang menghantar labelnya sendiri
       mengatasinya seperti biasa. */
    label = null,
    tinggi = 30,
    autoFocus = false,
    disabled = false,
    onKeyDown,
}) {
    /* Ketiga-tiga ayat di sini dibaca oleh PEMBACA SKRIN sahaja — tiada
       yang kelihatan pada skrin. Ia tetap diterjemah: pengguna yang
       bergantung pada pembaca skrin ialah orang yang paling terjejas
       apabila antara muka bercakap dua bahasa serentak. */
    const t = useT();
    const namaMedan = label || t('workspace.tiktok.timeLabel');
    /* Nilai yang masuk dilaraskan dahulu. Tanpa ini, waktu lama yang
       membawa minit ganjil tiada pilihan yang sepadan dalam senarai —
       <select> akan memaparkan pilihan pertama (00) dan waktu sebenar
       pengguna lenyap dari skrin tanpa dia sedar. */
    const [jam, minit] = laraskanJam(value).split(':');

    const hantar = (j, m) => onChange?.(`${j}:${m}`);

    return (
        <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}
            onKeyDown={onKeyDown}
        >
            <PilihV39
                ariaLabel={t('workspace.tiktok.timeHour', { label: namaMedan })}
                nilai={jam}
                disabled={disabled}
                autoFocus={autoFocus}
                onPilih={(j) => hantar(j, minit)}
                pilihan={JAM_PILIHAN.map((h) => ({ id: h, label: h }))}
                lebar={LEBAR_ANGKA}
                lebarMenu={92}
                tinggi={tinggi}
                saizTeks={13}
            />

            <span aria-hidden="true" style={{ fontSize: 12, color: 'var(--s-ink-3)' }}>:</span>

            <PilihV39
                ariaLabel={t('workspace.tiktok.timeMinute', { label: namaMedan })}
                nilai={minit}
                disabled={disabled}
                onPilih={(m) => hantar(jam, m)}
                pilihan={MINIT_PILIHAN.map((m) => ({ id: m, label: m }))}
                lebar={LEBAR_ANGKA}
                lebarMenu={92}
                tinggi={tinggi}
                saizTeks={13}
            />
        </span>
    );
}
